import React, { useEffect, useCallback, useMemo } from 'react';
import { useShellRootContext } from '../../contexts/ShellRoot';
import { useFinishExecution } from 'src/hooks/useFinishExecution';
import { Result } from 'src/types/result';
import { SIMPLEUI_EVENT_MAP } from 'src/constants/SimpleUIEvents';
import { BackFromHpIdType, ScreenAction } from 'src/analytics/types';
import { publishEvent } from 'src/analytics/publishEvent';
import { Logger } from 'src/utils/Logger';
import { ErrorCodes } from 'src/types/error';
import { HpxLoadingScreen } from '../HpxLoadingScreen';
import { FinishingSetupScreen } from '../FinishingSetupScreen';
import { InitialLoadingContent } from './InitialLoadingContent';
import { useLogin } from 'src/hooks/useLogin';
import { useShellAuthenticationData } from 'src/utils/shell';
import { useFinishingSetupScreenConfig } from 'src/hooks/useFinishingSetupScreenConfig';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const LoadingScreen = ({ flow }) => {
  const { setResultData } = useFinishExecution();
  const { retryAuthentication, cancelAuthentication } = useLogin();
  const {
    interfaces: { v1 }
  } = useShellRootContext();
  const { isLoggedIn } = useShellAuthenticationData(React);
  const { shouldUseNativeFlowUI } = useFinishingSetupScreenConfig();

  // Show the sign-in UI for any context except WEB_PORTAL (e.g., HPX, HPSmart, EasyStart, etc.)
  const appContext = v1.app?.getApplicationContext?.();
  const isWebPortal = appContext === 'WEB_PORTAL';

  /**
   * Handles authentication errors and cancellation scenarios.
   * Publishes a "back from HP ID" analytics event, persists the result data for
   * later use by finishExecution, and then cancels the active authentication request
   * so the pending signIn() promise resolves and the flow can terminate cleanly.
   *
   * @async
   * @param {Result} [result=Result.Declined] - The result to record (Declined or Failure).
   * @param {Object} errorObject - The error details to persist.
   * @param {string} errorObject.errorCode - Machine-readable error code.
   * @param {string} errorObject.message - Human-readable error message.
   * @returns {Promise<void>}
   */
  const errorCallback = useCallback(
    async (result = Result.Declined, errorObject) => {
      //event analytics - send event Simple UI
      publishEvent({
        type: ScreenAction.BACK_FROM_HP_ID,
        payload: {
          actionAuxParams: `status=${BackFromHpIdType.NOT_SIGNED_IN}`
        }
      });

      // Sets the result information
      Logger.log('Calling setResultData with:', { result, errorObject });
      setResultData(result, errorObject);

      // Cancels the authentication request which will trigger the pending signIn() promise to resolve and allow the flow to terminate cleanly.
      cancelAuthentication();
    },
    [setResultData, cancelAuthentication]
  );

  /**
   * Handles the user tapping the try again button on the non-HPX loading screen (e.g. InitialLoadingContent).
   * Despite the name, this does not retry authentication directly — it cancels the active
   * auth request, which causes the pending signIn() promise to resolve. The useLogin hook
   * then picks up the resolved response and closes the flow.
   *
   * @async
   * @returns {Promise<void>}
   */
  const handleOldTryAgain = useCallback(async () => {
    await cancelAuthentication();
  }, [cancelAuthentication]);

  /**
   * Handles the user tapping the cancel button on the HPX loading screen.
   * Delegates to errorCallback with a Declined result and a UserCanceledError,
   * which persists the result data and cancels the active authentication request
   * to terminate the flow cleanly.
   */
  const handleCancel = useCallback(() => {
    errorCallback(Result.Declined, {
      errorCode: ErrorCodes.UserCanceledError,
      message: 'User tapped the cancel button.'
    });
  }, [errorCallback]);

  // Timeout logic to prevent the loading screen from being displayed indefinitely.
  useEffect(() => {
    if (flow !== SIMPLEUI_EVENT_MAP.LOADING_SPINNER.INITIAL) return;

    const timeoutId = setTimeout(
      () => {
        Logger.error(
          'Loading screen timeout reached. Redirecting to error callback.'
        );
        errorCallback(Result.Failure, {
          errorCode: ErrorCodes.LoadingScreenTimeoutError,
          message:
            'Loading screen timeout reached. The operation took too long to complete.'
        });
      },
      1000 * 60 * 15 // 15 minutes
    );

    return () => clearTimeout(timeoutId);
  }, [errorCallback, flow]);

  // Determine whether to show the HPX loading screen based on the current flow and theme.
  const showHpxLoadingScreen = useMemo(() => {
    const isInitialFlow = [
      SIMPLEUI_EVENT_MAP.LOADING_SPINNER.MFE_INIT,
      SIMPLEUI_EVENT_MAP.LOADING_SPINNER.INITIAL
    ].includes(flow);

    const isPostSignInFlow =
      flow ===
      SIMPLEUI_EVENT_MAP.LOADING_SPINNER.POST_SIGN_IN_OR_CREATE_ACCOUNT;

    const shouldShow = !isWebPortal && (isInitialFlow || isPostSignInFlow);

    return shouldShow;
  }, [isWebPortal, flow]);

  if (showHpxLoadingScreen)
    return (
      <HpxLoadingScreen
        isLoggedIn={isLoggedIn}
        handleTryAgain={retryAuthentication}
        handleCancel={handleCancel}
      />
    );

  if (
    flow === SIMPLEUI_EVENT_MAP.LOADING_SPINNER.INITIAL &&
    shouldUseNativeFlowUI
  ) {
    return <InitialLoadingContent onTryAgain={handleOldTryAgain} />;
  }

  if (!isWebPortal) {
    return <FinishingSetupScreen />;
  }

  return <LoadingSpinner />;
};
